// @ts-nocheck
/**
 * 市場連鎖システム - 連鎖効果生成ロジック
 *
 * メインニュースから連鎖効果を生成する
 */

import {
  CHAIN_RATES,
  CHAIN_PROBABILITIES,
  CHAIN_LIMITS,
  CHAIN_PRIORITY,
  THEME_CATEGORY_MAPPING,
  CHAIN_RELATIONS,
  RIVALRY_PAIRS,
  TRIANGLE_CHAINS,
  ECOSYSTEM_CHAINS,
  DOMINION_CHAINS,
  TECHNOLOGY_CHAINS,
  getThemeFromCategoryTag,
  getThemeFromNamePattern,
  getCategoryTagsForTheme,
  getNamePatternsForTheme,
  getAttributeForTheme,
} from './chainRelations';

import {
  getPositiveChainTemplate,
  getCounterChainTemplate,
  getNegativeChainTemplate,
  getRivalryChainTemplate,
  getAttributeRippleTemplate,
  TRIANGLE_CHAIN_TEMPLATES,
  DOMINION_CHAIN_TEMPLATES,
  TECHNOLOGY_CHAIN_TEMPLATES,
  ECOSYSTEM_CHAIN_TEMPLATES,
} from './chainTemplates';

// ========================================
// ユーティリティ
// ========================================

/**
 * 配列からランダムに1つ選択
 */
const randomChoice = (arr) => arr[Math.floor(Math.random() * arr.length)];

/**
 * 確率判定
 * @param {number} probability - 0〜1の確率
 * @returns {boolean}
 */
const rollProbability = (probability) => Math.random() < probability;

// ========================================
// テーマ抽出
// ========================================

/**
 * ニュースのターゲットからテーマを抽出
 * @param {Object} news - ニュースオブジェクト
 * @returns {string|null} テーマ名
 */
export const getThemeFromNews = (news) => {
  if (!news || !news.target) return null;

  const target = news.target;

  // カテゴリからテーマを検索
  if (target.category) {
    // 【】を除去
    const categoryName = target.category.replace(/【|】/g, '');
    const theme = getThemeFromCategoryTag(categoryName);
    if (theme) return theme;
  }

  // 名称パターンからテーマを検索
  if (target.nameIncludes) {
    const theme = getThemeFromNamePattern(target.nameIncludes);
    if (theme) return theme;
  }

  // 属性から関連テーマを検索（最初にマッチしたもの）
  if (target.attribute) {
    for (const [theme, mapping] of Object.entries(THEME_CATEGORY_MAPPING)) {
      const attr = mapping.attribute || mapping.attributes?.[0];
      if (attr === target.attribute) {
        return theme;
      }
    }
  }

  return null;
};

/**
 * テーマからターゲットオブジェクトを生成
 * @param {string} theme - テーマ名
 * @returns {Object} ターゲットオブジェクト
 */
const createTargetFromTheme = (theme) => {
  const mapping = THEME_CATEGORY_MAPPING[theme];
  if (!mapping) return { nameIncludes: theme };

  // 名称パターンを優先
  if (mapping.namePatterns && mapping.namePatterns.length > 0) {
    return { nameIncludes: mapping.namePatterns[0] };
  }

  // カテゴリタグを使用
  if (mapping.categoryTags && mapping.categoryTags.length > 0) {
    return { category: mapping.categoryTags[0] };
  }

  return { nameIncludes: theme };
};

// ========================================
// 連鎖効果オブジェクト生成
// ========================================

/**
 * 連鎖効果オブジェクトを生成
 * @param {string} type - 連鎖タイプ
 * @param {string} targetTheme - 連鎖先テーマ
 * @param {string} sourceTheme - 元テーマ
 * @param {number} sourceModifier - 元の変動率
 * @param {string} text - 表示テキスト
 * @param {number} [customRate] - カスタム連鎖率
 * @returns {Object} 連鎖効果オブジェクト
 */
const createChainEffect = (type, targetTheme, sourceTheme, sourceModifier, text, customRate = null) => {
  const rate = customRate !== null ? customRate : CHAIN_RATES[type] || CHAIN_RATES.positive;
  const modifier = Math.round(sourceModifier * rate);

  return {
    type,
    theme: targetTheme,
    modifier,
    text,
    sourceTheme,
    sourceModifier,
    target: createTargetFromTheme(targetTheme),
  };
};

// ========================================
// 特殊連鎖チェック
// ========================================

/**
 * 宿敵連鎖をチェック
 * @param {string} theme - テーマ
 * @returns {Object|null} 宿敵ペア
 */
const checkRivalryChain = (theme) => {
  return RIVALRY_PAIRS.find(pair => pair.themeA === theme || pair.themeB === theme) || null;
};

/**
 * 三つ巴連鎖をチェック（起点のみ）
 * @param {string} theme - テーマ
 * @returns {Object|null} 三つ巴パターン
 */
const checkTriangleChain = (theme) => {
  return TRIANGLE_CHAINS.find(chain => chain.themes[0] === theme) || null;
};

/**
 * 覇権連鎖をチェック（支配者のみ）
 * @param {string} theme - テーマ
 * @returns {Object|null} 覇権定義
 */
const checkDominionChain = (theme) => {
  return DOMINION_CHAINS.find(dom => dom.ruler === theme) || null;
};

/**
 * 技術連鎖をチェック
 * @param {string} theme - テーマ
 * @returns {Object|null} 技術連鎖
 */
const checkTechnologyChain = (theme) => {
  return TECHNOLOGY_CHAINS.find(chain => chain.source === theme) || null;
};

/**
 * 生態系連鎖をチェック
 * @param {string} theme - テーマ
 * @returns {Object|null} 生態系連鎖
 */
const checkEcosystemChain = (theme) => {
  return ECOSYSTEM_CHAINS.find(chain => chain.source === theme) || null;
};

// ========================================
// 特殊連鎖生成
// ========================================

/**
 * 宿敵連鎖を生成
 * @param {Object} rivalryPair - 宿敵ペア
 * @param {string} sourceTheme - 元テーマ
 * @param {number} sourceModifier - 元の変動率
 * @returns {Object[]} 連鎖効果配列
 */
const generateRivalryChain = (rivalryPair, sourceTheme, sourceModifier) => {
  if (!rollProbability(CHAIN_PROBABILITIES.rivalry)) return [];

  const targetTheme = rivalryPair.themeA === sourceTheme ? rivalryPair.themeB : rivalryPair.themeA;
  const text = getRivalryChainTemplate(sourceTheme, targetTheme);

  return [createChainEffect('rivalry', targetTheme, sourceTheme, sourceModifier, text, CHAIN_RATES.rivalry)];
};

/**
 * 三つ巴連鎖を生成
 * @param {Object} triangleChain - 三つ巴パターン
 * @param {string} sourceTheme - 元テーマ
 * @param {number} sourceModifier - 元の変動率
 * @returns {Object[]} 連鎖効果配列
 */
const generateTriangleChain = (triangleChain, sourceTheme, sourceModifier) => {
  if (!rollProbability(CHAIN_PROBABILITIES.triangle)) return [];

  const chains = [];
  const templateKey = triangleChain.themes.join('→');
  const templates = TRIANGLE_CHAIN_TEMPLATES[templateKey];

  // 1段目
  const theme1 = triangleChain.themes[1];
  const text1 = templates?.step1 ? randomChoice(templates.step1) : `${sourceTheme}の影響で${theme1}も動く`;
  chains.push(createChainEffect('triangle', theme1, sourceTheme, sourceModifier, text1, CHAIN_RATES.triangle_1));

  // 2段目（確率で追加）
  if (rollProbability(0.6)) {
    const theme2 = triangleChain.themes[2];
    const text2 = templates?.step2 ? randomChoice(templates.step2) : `さらに${theme2}にも波及`;
    chains.push(createChainEffect('triangle', theme2, sourceTheme, sourceModifier, text2, CHAIN_RATES.triangle_2));
  }

  return chains;
};

/**
 * 覇権連鎖を生成
 * @param {Object} dominionChain - 覇権定義
 * @param {string} sourceTheme - 元テーマ（支配者）
 * @param {number} sourceModifier - 元の変動率
 * @returns {Object[]} 連鎖効果配列
 */
const generateDominionChain = (dominionChain, sourceTheme, sourceModifier) => {
  if (!rollProbability(CHAIN_PROBABILITIES.dominion)) return [];

  const chains = [];
  const templateKey = `${sourceTheme}（${dominionChain.domain}）`;
  const templates = DOMINION_CHAIN_TEMPLATES[templateKey];

  // 従属者への正連鎖（最大1つ）
  const vassals = dominionChain.vassals || [];
  if (vassals.length > 0 && rollProbability(0.7)) {
    const vassal = randomChoice(vassals);
    const vassalTemplates = templates?.vassals?.[vassal];
    const text = vassalTemplates ? randomChoice(vassalTemplates) : `${sourceTheme}の影響で${vassal}も上昇`;
    chains.push(createChainEffect('dominion_vassal', vassal, sourceTheme, sourceModifier, text, CHAIN_RATES.dominion_vassal));
  }

  // 対立者への負連鎖（最大1つ）
  const rivals = dominionChain.rivals || [];
  if (rivals.length > 0 && rollProbability(0.5)) {
    const rival = randomChoice(rivals);
    const rivalTemplates = templates?.rivals?.[rival];
    const text = rivalTemplates ? randomChoice(rivalTemplates) : `${sourceTheme}の台頭で${rival}は下落`;
    chains.push(createChainEffect('dominion_rival', rival, sourceTheme, sourceModifier, text, CHAIN_RATES.dominion_rival));
  }

  return chains;
};

/**
 * 技術連鎖を生成
 * @param {Object} techChain - 技術連鎖
 * @param {string} sourceTheme - 元テーマ
 * @param {number} sourceModifier - 元の変動率
 * @returns {Object[]} 連鎖効果配列
 */
const generateTechnologyChain = (techChain, sourceTheme, sourceModifier) => {
  if (!rollProbability(CHAIN_PROBABILITIES.technology)) return [];

  const chains = [];
  const templateKey = `${sourceTheme}→${techChain.target || techChain.targets?.[0]}`;
  const templates = TECHNOLOGY_CHAIN_TEMPLATES[templateKey];

  // 単一ターゲット
  if (techChain.target) {
    const text = templates ? randomChoice(templates) : `${sourceTheme}の技術が${techChain.target}に波及`;
    chains.push(createChainEffect('technology', techChain.target, sourceTheme, sourceModifier, text, CHAIN_RATES.technology));
  }

  // 複数ターゲット（未来鴉→呪術狩り/魔女）
  if (techChain.targets) {
    for (const target of techChain.targets) {
      if (rollProbability(0.5)) {
        const key = `${sourceTheme}→${target}`;
        const targetTemplates = TECHNOLOGY_CHAIN_TEMPLATES[key];
        const text = targetTemplates ? randomChoice(targetTemplates) : `${sourceTheme}の情報が${target}に`;
        chains.push(createChainEffect('technology', target, sourceTheme, sourceModifier, text, CHAIN_RATES.counter));
        break; // 1つまで
      }
    }
  }

  return chains;
};

/**
 * 生態系連鎖を生成
 * @param {Object} ecoChain - 生態系連鎖
 * @param {string} sourceTheme - 元テーマ
 * @param {number} sourceModifier - 元の変動率
 * @returns {Object[]} 連鎖効果配列
 */
const generateEcosystemChain = (ecoChain, sourceTheme, sourceModifier) => {
  if (!rollProbability(CHAIN_PROBABILITIES.ecosystem)) return [];

  const templateKey = `${sourceTheme}→${ecoChain.target}`;
  const templates = ECOSYSTEM_CHAIN_TEMPLATES[templateKey];
  const direction = ecoChain.direction;

  const rate = direction === 'positive' ? CHAIN_RATES.ecosystem : CHAIN_RATES.ecosystem_negative;
  const type = direction === 'positive' ? 'ecosystem' : 'ecosystem_negative';

  const templateList = templates?.[direction];
  const text = templateList ? randomChoice(templateList) : `${sourceTheme}の影響で${ecoChain.target}が${direction === 'positive' ? '活性化' : '抑制'}`;

  return [createChainEffect(type, ecoChain.target, sourceTheme, sourceModifier, text, rate)];
};

/**
 * 属性波及連鎖を生成
 * テーマ（カテゴリ/名称）からその属性全体への波及
 * @param {string} sourceTheme - 元テーマ
 * @param {number} sourceModifier - 元の変動率
 * @returns {Object[]} 連鎖効果配列
 */
const generateAttributeRippleChain = (sourceTheme, sourceModifier) => {
  if (!rollProbability(CHAIN_PROBABILITIES.attribute_ripple)) return [];

  // テーマから属性を取得
  const attribute = getAttributeForTheme(sourceTheme);
  if (!attribute) return [];

  // 属性が配列の場合は最初の属性のみ使用（例: フェニックス → 炎, 光 の場合は炎）
  const mainAttribute = Array.isArray(attribute) ? attribute[0] : attribute;

  // 'なし'属性はFX/ファンドに影響しないためスキップ
  if (mainAttribute === 'なし') return [];

  const text = getAttributeRippleTemplate(sourceTheme, mainAttribute);

  return [{
    type: 'attribute_ripple',
    theme: `${mainAttribute}属性`,
    modifier: Math.round(sourceModifier * CHAIN_RATES.attribute_ripple),
    text,
    sourceTheme,
    sourceModifier,
    target: { attribute: mainAttribute }, // 属性ターゲット → FX/ファンドに影響
  }];
};

// ========================================
// 基本連鎖生成
// ========================================

/**
 * 基本連鎖を生成
 * @param {string} sourceTheme - 元テーマ
 * @param {number} sourceModifier - 元の変動率
 * @param {number} remainingSlots - 残り連鎖枠数
 * @returns {Object[]} 連鎖効果配列
 */
const generateBasicChains = (sourceTheme, sourceModifier, remainingSlots) => {
  const relations = CHAIN_RELATIONS[sourceTheme];
  if (!relations) return [];

  const chains = [];
  let positiveCount = 0;
  let counterCount = 0;
  let negativeCount = 0;

  // 正連鎖（最大2個）
  for (const target of relations.positive || []) {
    if (chains.length >= remainingSlots) break;
    if (positiveCount >= CHAIN_LIMITS.maxPositive) break;
    if (rollProbability(CHAIN_PROBABILITIES.positive)) {
      const text = getPositiveChainTemplate(sourceTheme, target);
      chains.push(createChainEffect('positive', target, sourceTheme, sourceModifier, text));
      positiveCount++;
    }
  }

  // 対抗連鎖（最大1個）
  for (const target of relations.counter || []) {
    if (chains.length >= remainingSlots) break;
    if (counterCount >= CHAIN_LIMITS.maxCounter) break;
    if (rollProbability(CHAIN_PROBABILITIES.counter)) {
      const text = getCounterChainTemplate(sourceTheme, target);
      chains.push(createChainEffect('counter', target, sourceTheme, sourceModifier, text));
      counterCount++;
    }
  }

  // 負連鎖（最大1個）
  for (const target of relations.negative || []) {
    if (chains.length >= remainingSlots) break;
    if (negativeCount >= CHAIN_LIMITS.maxNegative) break;
    if (rollProbability(CHAIN_PROBABILITIES.negative)) {
      const text = getNegativeChainTemplate(sourceTheme, target);
      chains.push(createChainEffect('negative', target, sourceTheme, sourceModifier, text));
      negativeCount++;
    }
  }

  return chains;
};

// ========================================
// メイン生成関数
// ========================================

/**
 * メインニュースから連鎖効果を生成
 * @param {Object} mainNews - メインニュース
 * @returns {Object[]} 連鎖効果の配列
 */
export const generateChainEffects = (mainNews) => {
  if (!mainNews) return [];

  // テーマを抽出
  const theme = getThemeFromNews(mainNews);
  if (!theme) return [];

  const sourceModifier = mainNews.modifier || 0;
  if (Math.abs(sourceModifier) < 5) return []; // 変動が小さい場合は連鎖なし

  const chains = [];

  // 優先順位に従って特殊連鎖をチェック
  for (const chainType of CHAIN_PRIORITY) {
    if (chains.length >= CHAIN_LIMITS.maxTotal) break;

    switch (chainType) {
      case 'rivalry': {
        const rivalryPair = checkRivalryChain(theme);
        if (rivalryPair) {
          chains.push(...generateRivalryChain(rivalryPair, theme, sourceModifier));
        }
        break;
      }

      case 'dominion': {
        const dominionChain = checkDominionChain(theme);
        if (dominionChain) {
          const domChains = generateDominionChain(dominionChain, theme, sourceModifier);
          chains.push(...domChains.slice(0, CHAIN_LIMITS.maxTotal - chains.length));
        }
        break;
      }

      case 'triangle': {
        const triangleChain = checkTriangleChain(theme);
        if (triangleChain) {
          const triChains = generateTriangleChain(triangleChain, theme, sourceModifier);
          chains.push(...triChains.slice(0, CHAIN_LIMITS.maxTotal - chains.length));
        }
        break;
      }

      case 'technology': {
        const techChain = checkTechnologyChain(theme);
        if (techChain) {
          const techChains = generateTechnologyChain(techChain, theme, sourceModifier);
          chains.push(...techChains.slice(0, CHAIN_LIMITS.maxTotal - chains.length));
        }
        break;
      }

      case 'ecosystem': {
        const ecoChain = checkEcosystemChain(theme);
        if (ecoChain) {
          chains.push(...generateEcosystemChain(ecoChain, theme, sourceModifier));
        }
        break;
      }

      case 'attribute_ripple': {
        // 属性波及連鎖（テーマ→属性への波及）
        const rippleChains = generateAttributeRippleChain(theme, sourceModifier);
        chains.push(...rippleChains.slice(0, CHAIN_LIMITS.maxTotal - chains.length));
        break;
      }

      case 'basic': {
        // 基本連鎖は残り枠で生成
        const remaining = CHAIN_LIMITS.maxTotal - chains.length;
        if (remaining > 0) {
          chains.push(...generateBasicChains(theme, sourceModifier, remaining));
        }
        break;
      }
    }
  }

  // 最大数を超えないように制限
  return chains.slice(0, CHAIN_LIMITS.maxTotal);
};

/**
 * サブニュースを生成（メインと異なる対象）
 * @param {Object} mainNews - メインニュース
 * @param {Function} generateDailyNews - ニュース生成関数
 * @param {Object[]} recentNews - 直近ニュース
 * @param {number} currentDay - 現在の日数
 * @returns {Object|null} サブニュース
 */
export const generateSubNews = (mainNews, generateDailyNews, recentNews = [], currentDay = 0) => {
  if (!generateDailyNews) return null;

  let subNews;
  let attempts = 0;
  const maxAttempts = 10;

  do {
    subNews = generateDailyNews(recentNews, currentDay);
    attempts++;

    // メインと同じ対象は避ける
    const isSameTarget =
      (subNews?.target?.attribute && mainNews?.target?.attribute &&
       subNews.target.attribute === mainNews.target.attribute) ||
      (subNews?.target?.category && mainNews?.target?.category &&
       subNews.target.category === mainNews.target.category) ||
      (subNews?.target?.nameIncludes && mainNews?.target?.nameIncludes &&
       subNews.target.nameIncludes === mainNews.target.nameIncludes);

    if (!isSameTarget) break;
  } while (attempts < maxAttempts);

  // サブニュースの変動幅を調整（メインより控えめ）
  if (subNews && subNews.modifier) {
    const absModifier = Math.abs(subNews.modifier);
    const sign = subNews.modifier > 0 ? 1 : -1;
    // 10〜20の範囲に調整
    subNews.modifier = sign * Math.min(20, Math.max(10, absModifier * 0.7));
  }

  return subNews;
};

// ========================================
// エクスポート
// ========================================

export default {
  getThemeFromNews,
  generateChainEffects,
  generateSubNews,
};
